import axios from "axios";
import { useCallback, useState } from "react";
import { environment } from "../../environment";
import { useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import {
    IApplyMockupPresetResult,
    ICreateMockupPresetBody,
    IMockupPresetGroup,
    IUpdateMockupPresetBody
} from "./types";

type UseMockupPresetsKeyFunctions = {
    listPresets: () => Promise<IMockupPresetGroup[] | null>;
    createPreset: (body: ICreateMockupPresetBody) => Promise<IMockupPresetGroup | null>;
    updatePreset: (id: number, body: IUpdateMockupPresetBody) => Promise<IMockupPresetGroup | null>;
    deletePreset: (id: number) => Promise<boolean>;
    applyPreset: (id: number, designId: number, productId?: number) => Promise<IApplyMockupPresetResult | null>;
};

type UseMockupPresets = [
    {
        presets: IMockupPresetGroup[];
        loading: boolean;
    },
    UseMockupPresetsKeyFunctions
];

// #region useMockupPresets
export const useMockupPresets = (): UseMockupPresets => {
    const [, authToken] = useLogin();
    const [, setSnackbar] = useSnackbar();
    const [presets, setPresets] = useState<IMockupPresetGroup[]>([]);
    const [loading, setLoading] = useState(false);

    const headers = { 'X-Wallmates-Auth': authToken };

    const listPresets = useCallback(async (): Promise<IMockupPresetGroup[] | null> => {
        setLoading(true);
        try {
            const response = await axios.get<IMockupPresetGroup[]>(
                `${environment.baseUrl}/mockup-presets`,
                { headers }
            );
            setPresets(response.data || []);
            return response.data || [];
        } catch (err: any) {
            const message = err?.response?.data?.message || 'Failed to load mockup presets';
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return null;
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [authToken]);

    const createPreset = useCallback(async (body: ICreateMockupPresetBody): Promise<IMockupPresetGroup | null> => {
        try {
            const response = await axios.post<IMockupPresetGroup>(
                `${environment.baseUrl}/mockup-presets`,
                body,
                { headers }
            );
            setPresets((prev) => [response.data, ...prev]);
            setSnackbar({ show: true, snackbarLevel: 'info', text: `Preset "${response.data.name}" saved` });
            return response.data;
        } catch (err: any) {
            const message = err?.response?.data?.message || 'Failed to save preset';
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return null;
        }
        // eslint-disable-next-line
    }, [authToken]);

    const updatePreset = useCallback(async (id: number, body: IUpdateMockupPresetBody): Promise<IMockupPresetGroup | null> => {
        try {
            const response = await axios.put<IMockupPresetGroup>(
                `${environment.baseUrl}/mockup-presets/${id}`,
                body,
                { headers }
            );
            setPresets((prev) => prev.map((p) => (p.id === id ? response.data : p)));
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Preset updated' });
            return response.data;
        } catch (err: any) {
            const message = err?.response?.data?.message || 'Failed to update preset';
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return null;
        }
        // eslint-disable-next-line
    }, [authToken]);

    const deletePreset = useCallback(async (id: number): Promise<boolean> => {
        try {
            await axios.delete(`${environment.baseUrl}/mockup-presets/${id}`, { headers });
            setPresets((prev) => prev.filter((p) => p.id !== id));
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Preset deleted' });
            return true;
        } catch (err: any) {
            const message = err?.response?.data?.message || 'Failed to delete preset';
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return false;
        }
        // eslint-disable-next-line
    }, [authToken]);

    const applyPreset = useCallback(async (id: number, designId: number, productId?: number): Promise<IApplyMockupPresetResult | null> => {
        try {
            const response = await axios.post<IApplyMockupPresetResult>(
                `${environment.baseUrl}/mockup-presets/${id}/apply`,
                { design_id: designId, product_id: productId },
                { headers }
            );
            const { queued, skipped, total } = response.data;
            const summary = skipped > 0
                ? `Queued ${queued} of ${total} mockups (${skipped} skipped)`
                : `Queued ${queued} mockup${queued === 1 ? '' : 's'} for generation`;
            setSnackbar({ show: true, snackbarLevel: 'info', text: summary });
            return response.data;
        } catch (err: any) {
            const message = err?.response?.data?.message || 'Failed to apply preset';
            setSnackbar({ show: true, snackbarLevel: 'error', text: message });
            return null;
        }
        // eslint-disable-next-line
    }, [authToken]);

    return [
        { presets, loading },
        { listPresets, createPreset, updatePreset, deletePreset, applyPreset }
    ];
};
